import React from 'react';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { DateTimeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../..';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Date Time',
  identifier: 'DateTime',
  command_id: 'utility.datetime',
  icon: 'datetime',
  background: '#f35c5c',
  anchors: [
    {
      label: 'Execute',
      id: 'utility.datetime.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'utility.datetime.execute.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Completed',
      id: 'utility.datetime.completed',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Output',
      id: 'utility.datetime.output',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: DateTimeState = {
  timezone: 'America/Chicago',
};

export const fields: NodeField[] = [
  {
    id: 'timezone',
    tab: '1',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Timezone,
    label: 'Timezone',
    description: `The timezone to get the date and time for.`,
    placeholder: 'America/Chicago (-06:00)',
    required: true,
  },
];

export const output: NodeOutput = {
  default: {
    utility: {
      datetime: {
        utc_offset: 'string',
        timezone: 'string',
        day_of_week: 'number',
        day_of_year: 'number',
        datetime: 'string',
        utc_datetime: 'string',
        unixtime: 'number',
        raw_offset: 'number',
        week_number: 'number',
        dst: 'boolean',
        abbreviation: 'string',
      },
    },
  },
  dynamic: null,
};

export const DateTime: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const command = commands.find((command) => command.node.id === id);

  const { nodeOutline } = useNodeOutline({ command, selected });
  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });

  if (!command) return null;
  if (command.config.command_id !== 'utility.datetime') return;

  const displayTitle = command?.state?.name;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
